import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RootState } from '../../../redux/store';
import { noop } from 'oskcore';
import { Box, Button, Modal, ModalBody, ModalFooter, ModalHeader, OSKThemeType, Typography } from 'oskcomponents';
import { useTheme } from 'styled-components';
import { StacItem } from 'stac-ts';
import { setStacItemPropertiesDisplay } from '../../../redux/modules/data/search';

type StacItemPropertiesModalProps = {
    /** The STAC item to display properties for */
    displayStacItem?: StacItem;
};

const StacItemPropertyModal = ({ displayStacItem }: StacItemPropertiesModalProps) => {
    const dispatch = useDispatch();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (displayStacItem) {
            setVisible(true);
        } else {
            setVisible(false);
        }
    }, [displayStacItem]);

    if (!visible || !displayStacItem) return <></>;

    const fileIdRegex = /(?:.*\/)(.*)-.*\./gm;
    const regexResult =
        'visual_rgb' in displayStacItem.assets ? fileIdRegex.exec(displayStacItem.assets['visual_rgb'].href) : [];
    const fileId = regexResult && regexResult.length > 1 ? regexResult[1] : '';
    const sequenceId = fileId.split('-')[0]; // digits before the first hyphen

    return (
        <Modal visible={visible}>
            <ModalHeader variant="primary">
                <Typography variant="body3">STAC Item Metadata</Typography>
            </ModalHeader>
            <ModalBody>
                <Box col style={{ maxHeight: '600px', width: '600px', overflowY: 'scroll' }}>
                    <MetadataProperty title="Sequence ID" value={sequenceId} />
                    <MetadataProperty title="File ID" value={fileId} />
                    <MetadataProperty title="Task ID" value={displayStacItem.properties['osk:task_id']} />
                    <MetadataProperty title="datetime" value={displayStacItem.properties.datetime} />
                    <MetadataProperty title="Scene ID" value={displayStacItem.id} />
                    <details>
                        <summary
                            style={{
                                fontSize: '1.313rem',
                                fontWeight: 'bold',
                            }}
                        >
                            Assets
                        </summary>
                        {Object.entries(displayStacItem.assets).map((v, idx) => {
                            const header_key = v[0].toString();
                            const header_value = v[1];

                            let value_display: any = header_value;
                            if (typeof header_value === 'object') {
                                value_display = Object.entries(header_value as object).map((v, idx) => {
                                    const asset_key = v[0].toString();
                                    const asset_value = v[1];

                                    const is_link =
                                        typeof asset_value === 'string'
                                            ? asset_value.startsWith('http:') || asset_value.startsWith('https:')
                                            : false;
                                    const file_name_regex = /(?:.*\/)(.*\.\w{2,4})/gm;
                                    const regex_result = file_name_regex.exec(asset_value.toString());
                                    const file_name = regex_result && regex_result.length > 1 ? regex_result[1] : '';

                                    if (Array.isArray(asset_value)) {
                                        return (
                                            <MetadataProperty
                                                key={`stac-asset-value-${idx}`}
                                                value={
                                                    <details open={asset_value.length === 1}>
                                                        <summary
                                                            style={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: 'bold',
                                                            }}
                                                        >
                                                            {asset_key} ({asset_value.length})
                                                        </summary>
                                                        <Box col>
                                                            {asset_value.map((value) => (
                                                                <Typography key={value} variant="body3">
                                                                    {is_link ? (
                                                                        <a
                                                                            href={value}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                        >
                                                                            {file_name ?? JSON.stringify(value)}
                                                                        </a>
                                                                    ) : (
                                                                        JSON.stringify(value)
                                                                    )}
                                                                </Typography>
                                                            ))}
                                                        </Box>
                                                    </details>
                                                }
                                            />
                                        );
                                    } else if (typeof asset_value === 'object') {
                                        const asset_object_value = JSON.stringify(asset_value);

                                        // Hide exceptionally large strings inside a <details> container
                                        if (asset_object_value.length > 100) {
                                            return (
                                                <MetadataProperty
                                                    key={`stac-asset-value-${idx}`}
                                                    value={
                                                        <details>
                                                            <summary style={{ fontSize: '.75rem', fontWeight: 'bold' }}>
                                                                {asset_key}
                                                            </summary>
                                                            <Typography variant="body3">
                                                                {is_link ? (
                                                                    <a
                                                                        href={asset_object_value}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                    >
                                                                        {file_name ?? asset_object_value}
                                                                    </a>
                                                                ) : (
                                                                    asset_object_value
                                                                )}
                                                            </Typography>
                                                        </details>
                                                    }
                                                />
                                            );
                                        } else {
                                            return (
                                                <MetadataProperty
                                                    key={`stac-asset-value-${idx}`}
                                                    title={asset_key}
                                                    value={
                                                        is_link ? (
                                                            <a
                                                                href={asset_object_value}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                {file_name ?? asset_object_value}
                                                            </a>
                                                        ) : (
                                                            asset_object_value
                                                        )
                                                    }
                                                />
                                            );
                                        }
                                    } else {
                                        return (
                                            <MetadataProperty
                                                key={`stac-asset-value-${idx}`}
                                                title={asset_key}
                                                value={
                                                    is_link ? (
                                                        <a href={asset_value} target="_blank" rel="noreferrer">
                                                            {file_name ?? asset_value.toString()}
                                                        </a>
                                                    ) : (
                                                        asset_value.toString()
                                                    )
                                                }
                                            />
                                        );
                                    }
                                });
                            }

                            return (
                                <MetadataProperty
                                    key={`stac-asset-value-${idx}`}
                                    titleVariant="heading5"
                                    title={header_key}
                                    value={value_display}
                                />
                            );
                        })}
                    </details>
                    <details style={{ paddingTop: '12px' }} open>
                        <summary
                            style={{
                                fontSize: '1.313rem',
                                fontWeight: 'bold',
                            }}
                        >
                            Properties
                        </summary>
                        {Object.entries(displayStacItem.properties).map((v, idx) => {
                            const header_key = v[0].toString();
                            const header_value = v[1];

                            if (!header_key) return <></>;

                            // If the value is an array, wrap it in a <details> tag
                            if (Array.isArray(header_value)) {
                                return (
                                    <MetadataProperty
                                        key={`stac-property-${idx}`}
                                        value={
                                            <details open={header_value.length === 1}>
                                                <summary
                                                    style={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {header_key} ({header_value.length})
                                                </summary>

                                                <Box col>
                                                    {(header_value as string[]).map((value, idx) => (
                                                        <Typography key={`${header_key}-${idx}`} variant="body3">
                                                            {JSON.stringify(value)}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            </details>
                                        }
                                    />
                                );
                            }

                            let value_display: any = header_value;
                            if (typeof header_value === 'object') {
                                const object_keys = Object.keys(header_value as object);
                                // @ts-ignore
                                if (typeof header_value[object_keys[0]] === 'object') {
                                    /*  The value is an object that we should break down
                                    NOTE: If there are ever more layers than this, we should
                                    rewrite it as a recursive function. */
                                    value_display = object_keys.map((key, idx) => (
                                        <details key={`${idx}`} open={object_keys.length === 1}>
                                            <summary
                                                style={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {key} ({object_keys.length})
                                            </summary>
                                            <Box col pl={24}>
                                                <ul>
                                                    {/* @ts-ignore */}
                                                    {Object.values(header_value[key]).map((value, idx) => (
                                                        /* @ts-ignore */
                                                        <li key={`${header_value[key]}-${idx}`}>{value}</li>
                                                    ))}
                                                </ul>
                                            </Box>
                                        </details>
                                    ));
                                } else {
                                    // The value is something we can show as a string
                                    value_display = Object.keys(value_display as object)
                                        .map((key) => `${key}: ${value_display[key]}`)
                                        .join(' | ');
                                }
                            }
                            return (
                                <MetadataProperty
                                    key={`stac-property-${idx}`}
                                    title={header_key}
                                    value={value_display}
                                />
                            );
                        })}
                    </details>
                </Box>
            </ModalBody>
            <ModalFooter>
                <Button
                    variant="action"
                    label="Close"
                    onClick={() => {
                        setVisible(false);
                        dispatch(setStacItemPropertiesDisplay(undefined));
                    }}
                />
            </ModalFooter>
        </Modal>
    );
};

const MetadataProperty = ({ title, value, titleVariant }: any) => {
    const theme = useTheme() as OSKThemeType;

    return (
        <Box
            col
            style={{
                padding: '8px 0px',
                borderBottom: `1px solid ${theme.colors.black200}`,
            }}
        >
            {title && (
                <Typography variant={titleVariant ?? 'caption1'} strong>
                    {title}
                </Typography>
            )}
            <Typography variant="body3">{value === '' ? '<unknown>' : value}</Typography>
        </Box>
    );
};

const mapStateToProps = (state: RootState) => {
    const { displayStacItem } = state.data.search;
    return { displayStacItem };
};

export default connect(mapStateToProps, noop)(StacItemPropertyModal);
